import React, { useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext";

const TIER_CONFIG = {
  STARTER: { name: 'Starter', price: '$299/mo', color: '#059669', bg: '#d1fae5' },
  PROFESSIONAL: { name: 'Professional', price: '$599/mo', color: '#2563eb', bg: '#dbeafe' },
  ENTERPRISE: { name: 'Enterprise', price: 'Custom', color: '#7c3aed', bg: '#ede9fe' }
};

const STATUS_CONFIG = {
  ACTIVE: { color: '#065f46', bg: '#d1fae5' },
  TRIALING: { color: '#1e40af', bg: '#dbeafe' },
  PAST_DUE: { color: '#92400e', bg: '#fef3c7' },
  CANCELED: { color: '#dc2626', bg: '#fef2f2' }
};

export default function SuperAdminOrganizations() {
  const { adminEmail } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsage, setOrgUsage] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', contactName: '', password: '', phone: '', city: '', state: '', website: '', tier: 'STARTER' });

  async function loadOrgs() {
    setLoading(true);
    try {
      const res = await fetch('/api/organization/all', { 
        headers: { 'x-admin-email': adminEmail, 'x-user-role': 'BROKER_ADMIN' } 
      });
      const data = await res.json();
      setOrganizations(data.organizations || []);
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadOrgs(); }, []);

  const createOrg = async () => {
    try {
      const res = await fetch('/api/organization', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setShowCreateModal(false);
      setForm({ name: '', email: '', contactName: '', password: '', phone: '', city: '', state: '', website: '', tier: 'STARTER' });
      loadOrgs();
      const pw = data.adminUser?.temporaryPassword || '(not returned)';
      const loginEmail = data.adminUser?.email || '';
      const msg = 'Broker created! Login: ' + loginEmail + ' / Password: ' + pw + ' at ' + window.location.origin + '/login (14-day trial)';
      alert(msg);
      try { navigator.clipboard.writeText(msg); } catch(e) {}
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const updateSubscription = async (orgId, tier, status) => {
    try {
      const res = await fetch(`/api/organization/${orgId}/subscription`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail, 'x-user-role': 'BROKER_ADMIN' },
        body: JSON.stringify({ tier, status })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      loadOrgs();
      if (selectedOrg && selectedOrg.id === orgId) {
        setSelectedOrg({ ...selectedOrg, tier, subscriptionStatus: status });
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const viewDetails = async (org) => {
    setSelectedOrg(org);
    setShowDetailModal(true);
    
    // Load usage stats
    try {
      const res = await fetch(`/api/organization/${org.id}/usage`, {
        headers: { 'x-admin-email': adminEmail, 'x-user-role': 'BROKER_ADMIN' }
      });
      const data = await res.json();
      setOrgUsage(data.usage);
    } catch (e) {
      console.error('Failed to load usage:', e);
    }
  };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: 28, fontWeight: 900 }}>🌐 All Organizations</h1>
          <p style={{ margin: 0, color: '#6b7280' }}>Manage all customer organizations and subscriptions</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} style={{ padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          + New Organization
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Total Organizations</div>
          <div style={{ fontSize: 32, fontWeight: 900 }}>{organizations.length}</div>
        </div>
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Active</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: '#10b981' }}>
            {organizations.filter(o => o.subscriptionStatus === 'ACTIVE').length}
          </div>
        </div>
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Total Users</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: '#3b82f6' }}>
            {organizations.reduce((sum, o) => sum + parseInt(o.userCount || 0), 0)}
          </div>
        </div>
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Total Loads</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: '#8b5cf6' }}>
            {organizations.reduce((sum, o) => sum + parseInt(o.shipmentCount || 0), 0)}
          </div>
        </div>
      </div>

      {/* Organizations Table */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 100px 80px 80px 80px 120px', gap: 12, padding: 16, background: '#f9fafb', fontWeight: 700, fontSize: 12, color: '#6b7280', textTransform: 'uppercase' }}>
          <div>Organization</div>
          <div>Email</div>
          <div>Tier</div>
          <div>Status</div>
          <div>Users</div>
          <div>Loads</div>
          <div>Actions</div>
        </div>
        
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
        ) : organizations.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No organizations found</div>
        ) : (
          organizations.map(org => {
            const tier = TIER_CONFIG[org.tier] || TIER_CONFIG.STARTER;
            const status = STATUS_CONFIG[org.subscriptionStatus] || STATUS_CONFIG.ACTIVE;
            return (
              <div key={org.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 100px 80px 80px 80px 120px', gap: 12, padding: 16, borderTop: '1px solid #f3f4f6', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{org.name}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>{org.city}{org.city && org.state ? ', ' : ''}{org.state}</div>
                </div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>{org.email}</div>
                <div>
                  <span style={{ padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, color: tier.color, background: tier.bg }}>
                    {org.tier}
                  </span>
                </div>
                <div>
                  <span style={{ padding: '4px 6px', borderRadius: 6, fontSize: 10, fontWeight: 600, color: status.color, background: status.bg }}>
                    {org.subscriptionStatus}
                  </span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{org.userCount || 0}</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{org.loadCount || 0}</div>
                <div>
                  <button 
                    onClick={() => viewDetails(org)}
                    style={{ padding: '6px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500 }}>
            <h2 style={{ margin: '0 0 20px 0' }}>Create New Organization</h2>
            
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Organization Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Acme Logistics" style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Email *</label>
                  <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="admin@acme.com" style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' }} />
                </div>
                <div>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Phone</label>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>City</label>
                  <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' }} />
                </div>
                <div>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>State</label>
                  <input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' }} />
                </div>
              </div>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Starting Tier</label>
                <select value={form.tier} onChange={e => setForm({ ...form, tier: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' }}>
                  <option value="STARTER">Starter - $299/mo (100 loads, 5 users)</option>
                  <option value="PROFESSIONAL">Professional - $599/mo (500 loads, 15 users)</option>
                  <option value="ENTERPRISE">Enterprise - Custom pricing</option>
                </select>
              </div>
            </div>
            
            <div style={{ marginTop: 16, padding: 12, background: '#eff6ff', borderRadius: 8, fontSize: 13, color: '#1e40af' }}>
              ℹ️ New organizations start with a 14-day free trial
            </div>
            
            <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreateModal(false)} style={{ padding: '10px 20px', background: 'white', border: '2px solid #e5e7eb', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={createOrg} style={{ padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Create Organization</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedOrg && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <h2 style={{ margin: '0 0 4px 0' }}>{selectedOrg.name}</h2>
                <p style={{ margin: 0, color: '#6b7280' }}>{selectedOrg.email}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} style={{ padding: '8px 12px', background: '#f3f4f6', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            {/* Organization Info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
              <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16 }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>CONTACT INFO</div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Email:</strong> {selectedOrg.email}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Phone:</strong> {selectedOrg.phone || '—'}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Website:</strong> {selectedOrg.website || '—'}
                </div>
                <div>
                  <strong>Location:</strong> {selectedOrg.city}{selectedOrg.city && selectedOrg.state ? ', ' : ''}{selectedOrg.state} {selectedOrg.zip}
                </div>
              </div>
              
              <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16 }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>SUBSCRIPTION</div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Tier</label>
                  <select 
                    value={selectedOrg.tier} 
                    onChange={e => updateSubscription(selectedOrg.id, e.target.value, selectedOrg.subscriptionStatus)}
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  >
                    <option value="STARTER">Starter - $299/mo</option>
                    <option value="PROFESSIONAL">Professional - $599/mo</option>
                    <option value="ENTERPRISE">Enterprise - Custom</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Status</label>
                  <select 
                    value={selectedOrg.subscriptionStatus} 
                    onChange={e => updateSubscription(selectedOrg.id, selectedOrg.tier, e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="TRIALING">Trialing</option>
                    <option value="PAST_DUE">Past Due</option>
                    <option value="CANCELED">Canceled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Usage Stats */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>USAGE STATISTICS</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
                <div style={{ background: '#f0fdf4', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: '#10b981' }}>{orgUsage?.totalUsers || selectedOrg.userCount || 0}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>Users</div>
                </div>
                <div style={{ background: '#eff6ff', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: '#3b82f6' }}>{orgUsage?.totalLoads || selectedOrg.loadCount || 0}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>Total Loads</div>
                </div>
                <div style={{ background: '#fef3c7', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: '#f59e0b' }}>{orgUsage?.loadsThisMonth || 0}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>This Month</div>
                </div>
                <div style={{ background: '#fce7f3', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: '#ec4899' }}>{orgUsage?.totalCompanies || 0}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>Companies</div>
                </div>
                <div style={{ background: '#ede9fe', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: '#8b5cf6' }}>{orgUsage?.totalContacts || 0}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>CRM Contacts</div>
                </div>
              </div>
            </div>

            {/* Timestamps */}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, display: 'flex', gap: 24, fontSize: 12, color: '#9ca3af' }}>
              <div><strong>Created:</strong> {new Date(selectedOrg.createdAt).toLocaleDateString()}</div>
              <div><strong>Last Updated:</strong> {new Date(selectedOrg.updatedAt).toLocaleDateString()}</div>
              <div><strong>Org ID:</strong> {selectedOrg.id}</div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDetailModal(false)} style={{ padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
